
const NOMINATIM = "https://nominatim.openstreetmap.org";
const HEADERS = { "User-Agent": "DraptorApp/1.0" };

const userCity = localStorage.getItem("userCity")?.toLowerCase() || "";

// ─────────────────────────────────────────────────────────────
// PARSE HELPERS
// ─────────────────────────────────────────────────────────────

export const parseLocality = (address = {}) =>
    address.suburb ||
    address.neighbourhood ||
    address.residential ||
    address.quarter ||
    address.city_district ||
    address.borough ||
    address.town ||
    address.village ||
    address.hamlet ||
    "";

export const parseCity = (address = {}) =>
    address.city ||
    address.town ||
    address.municipality ||
    address.state_district ||
    "";

// ─────────────────────────────────────────────────────────────
// CACHE
// ─────────────────────────────────────────────────────────────

const cache = new Map();

// ─────────────────────────────────────────────────────────────
// FILTER CITIES / LOCALITIES
// ─────────────────────────────────────────────────────────────

export const filterCities = async (search) => {
    if (!search || search.length < 2) return [];

    const key = search.toLowerCase().trim();

    // FAST CACHE
    if (cache.has(key)) {
        return cache.get(key);
    }

    try {
        const res = await fetch(
            `${NOMINATIM}/search?` +
            `format=json` +
            `&q=${encodeURIComponent(search)}` +
            `&countrycodes=in` +
            `&addressdetails=1` +
            `&limit=20`,
            {
                headers: HEADERS,
            }
        );

        const data = await res.json();

        const seen = new Set();
        const results = [];

        for (const item of data) {
            const address = item.address || {};

            const allowedTypes = [
                "suburb",
                "neighbourhood",
                "residential",
                "quarter",
                "city_district",
                "borough",
                "town",
                "village",
                "hamlet",
            ];

            if (!allowedTypes.includes(item.type)) {
                continue;
            }

            const locality =
                address.suburb ||
                address.neighbourhood ||
                address.residential ||
                address.quarter ||
                address.city_district ||
                address.borough ||
                address.town ||
                address.village ||
                address.hamlet ||
                "";

            const city =
                address.city ||
                address.town ||
                address.municipality ||
                address.state_district ||
                "";

            if (!locality || locality.length < 2) {
                continue;
            }

            const value = locality.trim();

            const label =
                city && locality.toLowerCase() !== city.toLowerCase()
                    ? `${locality}, ${city}`
                    : locality;

            const uniqueKey = `${value}-${city}`.toLowerCase();

            if (seen.has(uniqueKey)) {
                continue;
            }

            seen.add(uniqueKey);

            const searchLower = search.toLowerCase();
            const localityLower = value.toLowerCase();
            const cityLower = city.toLowerCase();

            let score = 0;

            // STRONG LOCALITY BOOST
            if (address.suburb) score += 120;
            if (address.neighbourhood) score += 110;
            if (address.residential) score += 100;
            if (address.quarter) score += 90;
            if (address.city_district) score += 80;
            if (address.borough) score += 70;

            // REMOVE ROAD/HIGHWAY RESULTS
            if (address.road) score -= 100;
            if (address.highway) score -= 150;

            // EXACT MATCH
            if (localityLower === searchLower) {
                score += 200;
            }

            // STARTS WITH
            if (localityLower.startsWith(searchLower)) {
                score += 100;
            }

            // CONTAINS
            if (localityLower.includes(searchLower)) {
                score += 60;
            }

            // USER CITY BOOST
            if (userCity && cityLower.includes(userCity)) {
                score += 80;
            }

            results.push({
                value,
                label,
                city,
                score,
            });
        }

        // FALLBACK RESULTS
        if (results.length < 5) {
            for (const item of data) {
                const fallback = item.display_name?.split(",")[0]?.trim();

                if (!fallback || fallback.length < 2) {
                    continue;
                }

                const fallbackKey = fallback.toLowerCase();

                if (seen.has(fallbackKey)) {
                    continue;
                }

                seen.add(fallbackKey);

                results.push({
                    value: fallback,
                    label: fallback,
                    city: "",
                    score: 1,
                });
            }
        }

        // SORTING
        results.sort((a, b) => {
            const aExact = a.value.toLowerCase() === search.toLowerCase();
            const bExact = b.value.toLowerCase() === search.toLowerCase();

            if (aExact && !bExact) return -1;
            if (!aExact && bExact) return 1;

            return b.score - a.score;
        });

        const unique = new Map();

        for (const item of results) {
            const key = item.value.toLowerCase();

            if (!unique.has(key)) {
                unique.set(key, item);
            }
        }

        const finalResults = Array.from(unique.values()).slice(0, 8);

        cache.set(key, finalResults);

        return finalResults;

    } catch (error) {
        console.error("Nominatim failed:", error);
        return [];
    }
};

// ─────────────────────────────────────────────────────────────
// GEO LOOKUP FOR MONGODB $near FILTER
// ─────────────────────────────────────────────────────────────

const latLngCache = new Map();

export const getLatLngForFilter = async (place) => {
    if (!place) return null;

    const cacheKey = place.trim().toLowerCase();

    if (latLngCache.has(cacheKey)) {
        return latLngCache.get(cacheKey);
    }

    try {
        const res = await fetch(
            `${NOMINATIM}/search` +
            `?format=json` +
            `&q=${encodeURIComponent(place)},India` +
            `&limit=1`,
            {
                headers: HEADERS,
            }
        );

        const data = await res.json();

        if (!data.length) {
            return null;
        }

        const result = {
            latitude: parseFloat(data[0].lat),
            longitude: parseFloat(data[0].lon),
        };

        latLngCache.set(cacheKey, result);

        return result;

    } catch (err) {
        console.error("[getLatLngForFilter]", err);
        return null;
    }
};
